<template>
  <div class="layout-container grid h-screen w-screen">
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader class="h-auto gap-1 border-b border-sidebar-border pb-3">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton as-child class="h-auto py-2">
                <router-link :to="{ name: 'University Course', params: { slug: course.slug } }">
                  <vue-feather type="chevron-left" size="16" class="shrink-0" />
                  <span class="truncate font-semibold">{{ course.name }}</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <p class="px-2 text-xs text-white/55">
            {{ course.course_sections.length }} modules · {{ totalLectures }} lessons
          </p>
          <div class="px-2 pb-1 pt-1">
            <CourseProgress :completed="completedLectures" :total="totalLectures" tone="dark" />
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup v-for="(section, sectionIndex) in course.course_sections" :key="section.id">
            <SidebarGroupLabel class="gap-2">
              <span
                class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F4F3E3] text-[10px] font-bold text-[#4D2542]"
              >
                {{ sectionIndex + 1 }}
              </span>
              <span class="truncate">{{ section.name }}</span>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem v-for="sectionLecture in section.lectures" :key="sectionLecture.id">
                  <SidebarMenuButton
                    as-child
                    :is-active="lecture.id === sectionLecture.id"
                    class="h-auto min-h-8 items-start gap-2.5 whitespace-normal py-2 leading-snug data-[active=true]:bg-[#F4F3E3] data-[active=true]:font-semibold data-[active=true]:text-[#4D2542] data-[active=true]:hover:bg-[#F4F3E3] data-[active=true]:hover:text-[#4D2542]"
                  >
                    <router-link
                      :to="{
                        name: 'University Lecture',
                        params: { course_slug: course.slug, order: sectionLecture.order },
                      }"
                    >
                      <vue-feather
                        :type="lectureIcon(sectionLecture)"
                        size="15"
                        class="mt-0.5 shrink-0"
                        :class="lectureIconClass(sectionLecture)"
                      />
                      <span class="min-w-0 flex-1">{{ sectionLecture.name }}</span>
                    </router-link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      <main class="w-full overflow-y-scroll bg-gray-50/50">
        <HeaderToolbar>
          <div class="flex min-w-0 items-center gap-4">
            <SidebarTrigger />
            <header class="truncate text-lg font-bold">{{ lecture.name }}</header>
          </div>
        </HeaderToolbar>
        <div class="flex-grow p-6 sm:p-8">
          <slot></slot>
        </div>
      </main>
    </SidebarProvider>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import HeaderToolbar from '@/components/layouts/HeaderToolbar.vue';
import CourseProgress from '@/components/agent/university/CourseProgress.vue';
import type { LectureEntity } from '@/api/entity_types/University';
import type { CourseWithSections, LectureWithAssets } from '@/api/agent/university/course';

const props = defineProps<{
  course: CourseWithSections;
  lecture: LectureEntity;
}>();

const lectureIcon = (sectionLecture: LectureWithAssets) => {
  if (sectionLecture.completed) return 'check-circle';
  if (sectionLecture.id === props.lecture.id) return 'play-circle';
  return 'circle';
};

const lectureIconClass = (sectionLecture: LectureWithAssets) => {
  if (sectionLecture.completed) return 'text-green-500';
  if (sectionLecture.id === props.lecture.id) return 'text-[#4D2542]';
  return 'text-muted-foreground/50';
};

const totalLectures = computed(() =>
  props.course.course_sections.reduce((sum, section) => sum + section.lectures.length, 0)
);

const completedLectures = computed(() =>
  props.course.course_sections.reduce(
    (sum, section) => sum + section.lectures.filter(lecture => lecture.completed).length,
    0
  )
);
</script>

<style>
.layout-container {
  grid-template-rows: 70px calc(100vh - 70px);
}
</style>
